import React, { useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  Flight as FlightIcon,
} from '@mui/icons-material';
import { DeviceLocationStatus, DroneCoverageResponse } from '../services/copApi';
import { COP_DEVICE_COLORS } from '../utils/deviceColors';
import { formatSpeedMph } from '../utils/speed';
import { resolveCameraYawDeg } from '../utils/cameraFootprint';
import {
  formatArea,
  formatBearing,
  formatDistanceFromHome,
  formatFov,
  formatGimbalMode,
  formatGimbalPitch,
  formatVerticalSpeed,
  satelliteQuality,
} from '../utils/droneTelemetry';

/**
 * Head-up telemetry readout for the selected aircraft.
 *
 * The map popup answers "what is this pin", which means it is anchored to the
 * pin, closes on the next click and is the wrong place to watch numbers change
 * during a flight. This sits in a fixed corner instead, so altitude, speed and
 * gimbal attitude stay readable while the operator pans and zooms — the same job
 * the OSD does on the pilot's controller.
 *
 * Every field is optional and simply absent when the aircraft has not reported
 * it, rather than rendering a row of dashes.
 */

interface DroneTelemetryOverlayProps {
  device: DeviceLocationStatus | null;
  /** Accumulated coverage for this aircraft, when it has been fetched. */
  coverage?: DroneCoverageResponse | null;
  /**
   * Distance from the right edge, so the caller can shift the panel aside when
   * it would land on top of another one. Defaults clear of the map's icon rail.
   */
  rightOffset?: number;
}

/**
 * Transparent enough to read the map underneath, since this sits over terrain the
 * operator is actively watching. The blur behind it is what keeps the numbers
 * legible at this opacity — without it, text over high-contrast satellite imagery
 * becomes hard to read.
 */
const PANEL_BG = 'rgba(18, 22, 28, 0.62)';

/** One of the three headline numbers, with its unit underneath. */
const Readout: React.FC<{ label: string; value: string | null; sub?: string | null }> = ({
  label,
  value,
  sub,
}) => (
  <Box sx={{ minWidth: 76 }}>
    <Typography
      sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem', letterSpacing: 0.8 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: '#fff',
        fontSize: '1.05rem',
        fontWeight: 700,
        lineHeight: 1.15,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value ?? '—'}
    </Typography>
    {sub && (
      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.62rem' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

/** A label/value pair in the lower grid. Renders nothing without a value. */
const Field: React.FC<{ label: string; value: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: 'rgba(255,255,255,0.92)',
          fontSize: '0.65rem',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const SATELLITE_COLORS: Record<string, string> = {
  good: '#4caf50',
  fair: '#ffb300',
  poor: '#f44336',
};

const DroneTelemetryOverlay: React.FC<DroneTelemetryOverlayProps> = ({
  device,
  coverage,
  rightOffset = 54,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!device) return null;

  const cameraYaw = resolveCameraYawDeg({
    gimbalYawDeg: device.gimbal_yaw_deg,
    gimbalYawRelDeg: device.gimbal_yaw_rel_deg,
    gimbalMode: device.gimbal_mode,
    headingDeg: device.bearing,
  });

  const sats = device.satellite_count;
  const satQuality = satelliteQuality(sats);
  const areaObserved =
    coverage && coverage.device_id === device.device_id
      ? formatArea(coverage.area_m2)
      : null;

  return (
    <Box
      sx={{
        position: 'absolute',
        // Top right, because the activity timeline docks across the bottom of the
        // map and covered this panel where it used to sit. The right offset clears
        // the icon rail on the map's right edge.
        top: 10,
        right: rightOffset,
        zIndex: 3,
        width: 236,
        backgroundColor: PANEL_BG,
        backdropFilter: 'blur(6px)',
        border: `1px solid ${COP_DEVICE_COLORS.drone}55`,
        borderRadius: 1,
        boxShadow: '0 4px 18px rgba(0,0,0,0.45)',
        px: 1.25,
        py: 1,
        pointerEvents: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: collapsed ? 0 : 0.75 }}>
        <FlightIcon sx={{ fontSize: 15, color: COP_DEVICE_COLORS.drone }} />
        <Typography
          sx={{
            color: '#fff',
            fontSize: '0.74rem',
            fontWeight: 700,
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {device.entity_id || device.device_id}
        </Typography>
        {device.is_streaming && (
          <Chip
            label="LIVE"
            size="small"
            sx={{ height: 15, fontSize: 9, fontWeight: 800, bgcolor: '#dc2626', color: '#fff' }}
          />
        )}
        {device.is_flying != null && (
          <Chip
            label={device.is_flying ? 'AIR' : 'GND'}
            size="small"
            sx={{
              height: 15,
              fontSize: 9,
              fontWeight: 800,
              bgcolor: device.is_flying ? `${COP_DEVICE_COLORS.drone}33` : 'rgba(255,255,255,0.1)',
              color: device.is_flying ? COP_DEVICE_COLORS.drone : 'rgba(255,255,255,0.6)',
            }}
          />
        )}
        <Tooltip title={collapsed ? 'Show telemetry' : 'Hide telemetry'}>
          <IconButton
            size="small"
            onClick={() => setCollapsed((c) => !c)}
            sx={{ color: 'rgba(255,255,255,0.5)', p: 0.15 }}
          >
            {collapsed ? <ExpandIcon sx={{ fontSize: 16 }} /> : <CollapseIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {!collapsed && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Readout
              label="ALT AGL"
              value={
                device.altitude_agl_m != null ? `${Math.round(device.altitude_agl_m)} m` : null
              }
              sub={
                device.altitude_agl_m != null
                  ? `${Math.round(device.altitude_agl_m / 0.3048)} ft`
                  : null
              }
            />
            <Readout
              label="SPEED"
              value={device.speed != null ? formatSpeedMph(device.speed) : null}
              sub={formatVerticalSpeed(device.vertical_speed_ms)}
            />
            <Readout
              label="HOME"
              value={formatDistanceFromHome(device)}
              sub={device.home_latitude != null ? 'range' : 'not set'}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              pt: 0.75,
              borderTop: '1px solid rgba(255,255,255,0.09)',
            }}
          >
            <Field label="Heading" value={formatBearing(device.bearing)} />
            <Field label="Camera" value={formatBearing(cameraYaw)} />
            <Field label="Gimbal" value={formatGimbalPitch(device.gimbal_pitch_deg)} />
            <Field label="Mode" value={formatGimbalMode(device.gimbal_mode)} />
            <Field label="FOV" value={formatFov(device.hfov_deg, device.vfov_deg)} />
            <Field label="Observed" value={areaObserved} />
            {sats != null && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.65rem' }}>
                  GPS
                </Typography>
                <Typography
                  sx={{
                    color: satQuality ? SATELLITE_COLORS[satQuality] : 'rgba(255,255,255,0.92)',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                  }}
                >
                  {sats} sats
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default DroneTelemetryOverlay;
